<template>
  <div class="registration-form__step registration-form__step-password">
    <div class="registration-form__step__container">
      <div class="fields">
        <div class="fields-password">
          <text-input-fi
            id="password"
            ref="passwordRef"
            v-model.trim="password"
            name="password"
            type="password"
            autocomplete="new-password"
            data-test="password"
            :rules="passwordRules"
            :maxlength="120"
            :label="fields.passwordLabel?.value"
            required
            @blur="activatePasswordValidation(passwordRef)"
          />

          <password-requirements
            :password="password"
            @valid-changed="passwordValidationChanged"
          />
        </div>
        <div class="fields-password-repeat">
          <text-input-fi
            id="password-repeat"
            ref="passwordRepeatRef"
            v-model.trim="passwordRepeat"
            name="password-repeat"
            type="password"
            autocomplete="new-password"
            data-test="password-repeat"
            :rules="
              enablePasswordRepeatValidation
                ? checkPasswordRepeatRules()
                : dynamicRule(rules.email, enablePasswordRepeatValidation)
            "
            :maxlength="120"
            :label="fields.passwordRepeatLabel?.value"
            required
            @blur="enablePasswordRepeatValidation = true"
            @change="passwordRepeatChanged"
            @keyup="passwordRepeatChanged"
          />
        </div>
      </div>

      <div class="terms">
        <checkbox-custom-fi
          :value="termsAndConditions"
          :rules="rules.termsAndConditions"
          class="registration-form__checkbox"
          data-test="terms-and-conditions"
          :data-test-2="
            termsAndConditions ? 'terms-and-conditions-checked' : 'terms-and-conditions-unchecked'
          "
          required
          @checkbox-custom-changed="(value) => (termsAndConditions = value)"
        >
          <!-- eslint-disable vue/no-v-html -->
          <span
            v-html="
              labelContent('termsAndConditionsCheckboxLabel', [
                'termsAndConditionsLink',
                'dataPrivacylink',
                'additionalTermsAndConditionsLink',
              ])
            "
          />
          <!-- eslint-enable vue/no-v-html -->
        </checkbox-custom-fi>

        <checkbox-custom-fi
          v-if="fields.enableAdvertisingAgreement?.value"
          :value="advertisingConsent"
          class="registration-form__checkbox"
          data-test="advertising-agreement"
          :data-test-2="
            advertisingConsent ? 'advertising-agreement-checked' : 'advertising-agreement-unchecked'
          "
          @checkbox-custom-changed="(value) => (advertisingConsent = value)"
        >
          <!-- eslint-disable vue/no-v-html -->
          <span
            v-html="labelContent('advertisingAgreementCheckboxLabel', ['advertisingAgreement'])"
          />
          <!-- eslint-enable vue/no-v-html -->
        </checkbox-custom-fi>

        <div
          v-if="!noRecaptcha"
          class="registration-form__recaptcha"
        >
          <vue-recaptcha
            ref="invisibleRecaptcha"
            data-test="receptcha"
            :sitekey="sitekey"
            @verify="onVerify"
            @error="onError"
            @is-valid="onCaptchaIsValid"
          />

          <div
            v-if="isCaptchaError && fields.googleCaptchaValidationMessage?.value"
            class="registration-form__recaptcha__error-message"
            :class="{ 'registration-form__recaptcha--error': isCaptchaError }"
          >
            {{ fields.googleCaptchaValidationMessage.value }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { CheckboxCustomFi, PasswordRequirements, TextInputFi } from 'atoms';
import { useRegistrationStore } from 'store/registration';
import { computed, nextTick, ref, useTemplateRef } from 'vue';

import VueRecaptcha from '@/components/vue-recaptcha/VueRecaptcha.vue';
import { useCheckboxCustomLabel } from '@/composables/checkboxCustomLabel';
import { useFields } from '@/composables/fields';
import { useDict, useFischer } from '@/composables/fischerPlugin';
import { useValidators } from '@/composables/validators';

defineOptions({ name: 'RegistrationStepPassword' });

const props = withDefaults(
  defineProps<{
    noRecaptcha?: boolean;
  }>(),
  {
    noRecaptcha: false,
  },
);

const emit = defineEmits<{
  'valid-changed': [isValid: boolean];
}>();

const fields = useFields('MyAccountRegisterFields');

const fischer = useFischer();
const dict = useDict();
const registrationStore = useRegistrationStore();
const { labelContent } = useCheckboxCustomLabel(fields);
const { rules } = useValidators();

const passwordRef = useTemplateRef<any>('passwordRef');
const passwordRepeatRef = useTemplateRef<any>('passwordRepeatRef');
const invisibleRecaptcha = useTemplateRef<any>('invisibleRecaptcha');

const enablePasswordValidation = ref(false);
const enablePasswordRepeatValidation = ref(false);
const passwordValid = ref(false);
const captchaValid = ref(false);

const sitekey = computed(() => fischer.recaptchaKey());

const password = computed({
  get: () => registrationStore.password,
  set: (val: string) => {
    registrationStore.password = val;
    validate();
  },
});

const passwordRepeat = computed({
  get: () => registrationStore.passwordRepeat,
  set: (val: string) => {
    registrationStore.passwordRepeat = val;
    validate();
  },
});

const termsAndConditions = computed({
  get: () => registrationStore.termsAndConditions,
  set: (val: boolean) => {
    registrationStore.termsAndConditions = val;
    validate();
  },
});

const advertisingConsent = computed({
  get: () => registrationStore.advertisingConsent,
  set: (val: boolean) => {
    registrationStore.advertisingConsent = val;
  },
});

const isCaptchaError = computed({
  get: () => registrationStore.isCaptchaError,
  set: (val: boolean) => {
    registrationStore.isCaptchaError = val;
    validate();
  },
});

const captchaResponse = computed({
  get: () => registrationStore.captchaResponse,
  set: (val: string) => {
    registrationStore.setCaptchaResponse(val);
    validate();
  },
});

const getField = (fieldName: keyof typeof fields) => fields[fieldName]?.value;

const checkPasswordRepeatRules = () => {
  return [
    (v: any) => !!v || getField('passwordFieldValidationMsgRepeat'),
    password.value === passwordRepeat.value ||
      getField('passwordFieldValidationMsgNoMatch') ||
      dict.get('online-Shop.Password.Match-Check'),
  ];
};

const passwordRules = computed(() => {
  const checkPasswordRule = [
    (v: any) =>
      (passwordValid.value && !!v) ||
      getField('passwordFieldValidationMsgRequired') ||
      dict.get('online-Shop.Global-Labels.Error-Message-Required'),
  ];
  return enablePasswordValidation.value ? checkPasswordRule : [];
});

const dynamicRule = (defaultRule: any, isDefaultRuleEnabled: boolean) =>
  isDefaultRuleEnabled ? defaultRule : [];

const passwordValidationChanged = (value: boolean) => {
  passwordValid.value = value;
};

const passwordRepeatChanged = () => {
  if (enablePasswordRepeatValidation.value) {
    return;
  }
  if (
    passwordRepeat.value !== null &&
    passwordRepeat.value !== undefined &&
    !password.value.startsWith(passwordRepeat.value)
  ) {
    enablePasswordRepeatValidation.value = true;
  }
};

const activatePasswordValidation = async (ref: any) => {
  enablePasswordValidation.value = true;
  await nextTick();
  ref?.validate?.();
};

const validate = () => {
  let isValid = false;
  if (passwordRef.value && passwordRepeatRef.value) {
    isValid = Boolean(
      password.value !== '' &&
      !passwordRef.value.errorBucket?.length &&
      passwordRepeat.value !== '' &&
      !passwordRepeatRef.value.errorBucket?.length &&
      termsAndConditions.value &&
      (props.noRecaptcha || captchaValid.value),
    );
  }
  emit('valid-changed', isValid);
};

const onVerify = (response: string) => {
  if (response) {
    isCaptchaError.value = false;
    captchaResponse.value = response;
  }
};

const onError = (response: any) => {
  if (response) {
    isCaptchaError.value = true;
  }
};

const resetRecaptcha = () => {
  invisibleRecaptcha.value?.reset();
};

const onCaptchaIsValid = (value: boolean) => {
  captchaValid.value = value;
  validate();
};

// created
if (password.value.length) {
  enablePasswordValidation.value = true;
}
if (passwordRepeat.value.length) {
  enablePasswordRepeatValidation.value = true;
}

defineExpose({ resetRecaptcha });
</script>

<style src="./registration-step-password-unscoped" />

<style scoped src="./registration-step-password" />
